import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, UserPlus, MessageCircle, Calendar, CreditCard, Star, Shield, BadgeCheck, Lock, Heart, Clock, MapPin, ArrowRight, Sparkles } from "lucide-react";

const STEPS = [
  { n: "1", icon: UserPlus, title: "Créez votre compte parent", desc: "Inscription gratuite en moins d'une minute, par email ou avec Google. Aucun engagement, aucun frais caché." },
  { n: "2", icon: Search, title: "Recherchez une baby-sitter", desc: "Indiquez votre ville, le créneau souhaité et vos critères. Découvrez les profils vérifiés près de chez vous." },
  { n: "3", icon: MessageCircle, title: "Échangez via la messagerie", desc: "Posez vos questions, vérifiez la disponibilité et faites connaissance avant de réserver." },
  { n: "4", icon: Calendar, title: "Réservez le créneau", desc: "Sélectionnez le jour et l'horaire directement depuis le profil de la nounou." },
  { n: "5", icon: CreditCard, title: "Réglez directement la baby-sitter", desc: "Le paiement de la garde se fait de gré à gré avec la nounou (espèces, CESU, virement, PayPal…). Click&Gard ne prend aucune commission." },
  { n: "6", icon: Star, title: "Validez et notez la garde", desc: "Après la prestation, validez la garde et laissez un avis pour la communauté." },
];

const FAQS = [
  { q: "Click&Gard est-il vraiment gratuit pour les parents ?", a: "Oui, 100% gratuit. Vous ne payez que la prestation de garde au tarif fixé par la baby-sitter. Aucun abonnement, aucune commission cachée côté parent." },
  { q: "Comment sont vérifiées les baby-sitters ?", a: "Chaque professionnel passe par une vérification d'identité (pièce d'identité + selfie), une validation manuelle par notre équipe et doit souscrire à un abonnement actif pour recevoir des demandes." },
  { q: "Que se passe-t-il si la baby-sitter annule ?", a: "Vous êtes intégralement remboursé(e) et notre équipe vous aide à trouver une alternative rapidement. Les annulations sont rares grâce à notre système d'engagement." },
  { q: "Puis-je réserver une garde en urgence ?", a: "Oui, filtrez les baby-sitters disponibles immédiatement. De nombreuses nounous acceptent des gardes le jour même ou en soirée." },
  { q: "Comment fonctionne le paiement ?", a: "Click&Gard ne gère pas les paiements de garde : vous réglez directement la baby-sitter selon les modalités convenues ensemble (espèces, CESU, virement, PayPal, etc.). La plateforme sert uniquement à la mise en relation et à la réservation." },
];

const CommentCaMarcheParents = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-immersive">
      <Seo
        title="Comment ça marche pour les parents"
        description="Découvrez comment trouver et réserver une baby-sitter de confiance sur Click&Gard en 6 étapes simples. Inscription gratuite, profils vérifiés, paiement sécurisé."
        path="/comment-ca-marche-parents"
        jsonLd={faqJsonLd}
      />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden py-20 md:py-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/10" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Heart className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">Espace parents</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
            Comment ça marche pour les parents ?
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Trouvez et réservez une baby-sitter vérifiée en quelques clics. Simple, rapide et 100% gratuit pour les parents.
          </p>
          <Link to="/auth?tab=register">
            <Button size="lg" variant="neon" className="text-base font-semibold">
              <UserPlus className="w-5 h-5" />
              Créer un compte parent gratuit
            </Button>
          </Link>
        </div>
      </section>

      {/* ETAPES */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-4">
            6 étapes pour trouver votre nounou
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Un parcours pensé pour vous faire gagner du temps tout en garantissant la sécurité de vos enfants.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((step) => (
              <article key={step.n} className="relative rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm p-6 hover:border-primary/40 hover:-translate-y-1 transition-all">
                <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground font-bold flex items-center justify-center shadow-lg">
                  {step.n}
                </div>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="py-16 px-4 bg-card/30 backdrop-blur-sm border-y border-border/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
            Vos avantages parents
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Heart, title: "100% gratuit", desc: "Aucun abonnement, aucune commission. Vous payez uniquement la prestation." },
              { icon: BadgeCheck, title: "Profils vérifiés", desc: "Identité contrôlée et validation manuelle de chaque baby-sitter." },
              { icon: Lock, title: "Mise en relation sécurisée", desc: "Profils vérifiés et messagerie modérée. Le paiement se règle directement avec la nounou." },
              { icon: Clock, title: "Réservation 24/7", desc: "Réservez à toute heure, même pour des gardes en urgence." },
              { icon: MapPin, title: "Partout en France", desc: "Une baby-sitter près de chez vous, en ville comme en zone rurale." },
              { icon: Star, title: "Avis vérifiés", desc: "Consultez les retours d'autres parents avant de réserver." },
            ].map((a) => (
              <div key={a.title} className="rounded-2xl border border-border/50 bg-card/50 p-6">
                <a.icon className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold mb-2">{a.title}</h3>
                <p className="text-sm text-muted-foreground">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITE */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Votre tranquillité d'esprit</h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Confier ses enfants n'est pas anodin. Notre processus de validation en 3 étapes (email, identité, abonnement actif) garantit que chaque baby-sitter visible sur la plateforme est sérieuse et vérifiée.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-left">
            {[
              "Vérification d'identité obligatoire",
              "Validation manuelle par notre équipe",
              "Service de médiation en cas de litige",
            ].map((t) => (
              <div key={t} className="flex items-start gap-3 p-4 rounded-xl bg-card/50 border border-border/50">
                <BadgeCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-card/30 backdrop-blur-sm border-y border-border/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-border/50 bg-card/50 backdrop-blur-sm px-5 border-b">
                <AccordionTrigger className="text-left font-semibold hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center relative overflow-hidden rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/10 via-secondary/10 to-accent/10 p-12 backdrop-blur-sm">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="relative">
            <Sparkles className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">Prêt à trouver votre baby-sitter ?</h2>
            <p className="text-lg text-muted-foreground mb-8">Inscription gratuite, sans engagement.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/auth?tab=register">
                <Button size="lg" variant="neon">Créer un compte parent</Button>
              </Link>
              <Link to="/search">
                <Button size="lg" variant="outline">
                  Explorer les profils
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommentCaMarcheParents;
